import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, X, ToggleLeft, ToggleRight, Eye, EyeOff, ExternalLink, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money, fmtDate, statusPill, statusLabel } from '../../lib/api';
import PullToRefresh from '../../components/PullToRefresh';
import SwipeRow from '../../components/SwipeRow';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([adminApi.get('/admin/customers'), adminApi.get('/admin/products')]);
    setCustomers(c.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const saveNew = async (data) => {
    try { await adminApi.post('/admin/customers', data); setAdding(false); load(); toast.success('Customer added'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveEdit = async (id, data) => {
    try { await adminApi.patch(`/admin/customers/${id}`, data); setEditing(null); load(); toast.success('Saved'); }
    catch { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try { await adminApi.delete(`/admin/customers/${id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const sendWelcome = async (c) => {
    if (!window.confirm(`Send welcome email to ${c.name} (${c.email})?`)) return;
    try {
      await adminApi.post(`/admin/customers/${c.customer_id}/send-welcome`);
      toast.success(`Welcome email sent to ${c.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    }
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div className="fade-in space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold">Customers</h1>
          <p className="text-sm text-[#6b7280] mt-1">{customers.length} customers</p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-brand flex items-center gap-2" data-testid="add-customer-btn"><Plus size={16} /> Add Customer</button>
      </div>

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Name / Company</th><th className="text-left px-4 py-3">Email</th><th className="text-right px-4 py-3">Billed</th><th className="text-right px-4 py-3">Paid</th><th className="text-right px-4 py-3">Balance</th><th className="text-center px-4 py-3">Partial</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr
                key={c.customer_id}
                className="border-t border-[#f3f3f3] hover:bg-[#fafafa] cursor-pointer"
                onClick={() => setEditing(c)}
                data-testid={`customer-row-${c.customer_id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-[#6b7280]">{c.company || '—'}</div>
                </td>
                <td className="px-4 py-3 text-[#6b7280]">{c.email}</td>
                <td className="text-right px-4 py-3 mono">{money(c.total_billed)}</td>
                <td className="text-right px-4 py-3 mono text-green-700">{money(c.total_paid)}</td>
                <td className="text-right px-4 py-3 mono text-[#d92027] font-semibold">{money(c.balance_due)}</td>
                <td className="text-center px-4 py-3">{c.allow_partial_payment ? <ToggleRight size={22} className="text-green-600 inline" /> : <ToggleLeft size={22} className="text-[#6b7280] inline" />}</td>
                <td className="text-right px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => sendWelcome(c)} className="p-1.5 hover:bg-white rounded text-[#6b7280]" title="Send welcome email" data-testid={`welcome-customer-${c.customer_id}`}><Mail size={14} /></button>
                  <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-white rounded" data-testid={`edit-customer-${c.customer_id}`}><Edit2 size={14} /></button>
                  <button onClick={() => del(c.customer_id)} className="p-1.5 hover:bg-white rounded text-[#d92027]" data-testid={`delete-customer-${c.customer_id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {customers.length === 0 && <div className="py-10 text-center text-sm text-[#6b7280]">No customers yet.</div>}
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {customers.length === 0 ? (
          <div className="card py-10 text-center text-sm text-[#6b7280]">No customers yet.</div>
        ) : (
          customers.map((c) => (
            <SwipeRow
              key={c.customer_id}
              onDelete={() => del(c.customer_id)}
              testid={`swipe-customer-${c.customer_id}`}
            >
            <div
              className="card p-4 cursor-pointer"
              onClick={() => setEditing(c)}
              data-testid={`mobile-customer-${c.customer_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{c.name}</div>
                  {c.company && c.company !== c.name && (
                    <div className="text-xs text-[#6b7280] truncate">{c.company}</div>
                  )}
                  <div className="text-xs text-[#6b7280] truncate mt-0.5">{c.email}</div>
                </div>
                <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => sendWelcome(c)} className="p-2 rounded-lg bg-[#fafafa] text-[#6b7280]" title="Send welcome" data-testid={`m-welcome-customer-${c.customer_id}`}><Mail size={16} /></button>
                  <button onClick={() => setEditing(c)} className="p-2 rounded-lg bg-[#fafafa] text-[#6b7280]" data-testid={`m-edit-customer-${c.customer_id}`}><Edit2 size={16} /></button>
                  <button onClick={() => del(c.customer_id)} className="p-2 rounded-lg bg-[#fef2f2] text-[#d92027]" data-testid={`m-delete-customer-${c.customer_id}`}><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-[#f3f3f3]">
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Billed</div>
                  <div className="mono font-semibold">{money(c.total_billed)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Paid</div>
                  <div className="mono font-semibold text-green-700">{money(c.total_paid)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Balance</div>
                  <div className={`mono font-semibold ${c.balance_due > 0 ? 'text-[#d92027]' : 'text-green-700'}`}>{money(c.balance_due)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs mt-3 pt-3 border-t border-[#f3f3f3] text-[#6b7280]">
                {c.allow_partial_payment ? <ToggleRight size={18} className="text-green-600" /> : <ToggleLeft size={18} />}
                <span>Partial payments {c.allow_partial_payment ? 'enabled' : 'off'}</span>
              </div>
            </div>
            </SwipeRow>
          ))
        )}
      </div>

      {adding && <CustomerFormModal onClose={() => setAdding(false)} onSave={saveNew} />}
      {editing && <CustomerEditModal customer={editing} products={products} onClose={() => setEditing(null)} onSave={(d) => saveEdit(editing.customer_id, d)} onReload={load} />}
    </div>
    </PullToRefresh>
  );
}

function CustomerFormModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState({
    email: customer?.email || '', name: customer?.name || '', company: customer?.company || '',
    phone: customer?.phone || '', address: customer?.address || '', city: customer?.city || '',
    state: customer?.state || '', zip_code: customer?.zip_code || '', notes: customer?.notes || '',
    allow_partial_payment: customer?.allow_partial_payment || false,
    additional_emails_raw: (customer?.additional_emails || []).join(', '),
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const handleSave = () => {
    const additional_emails = form.additional_emails_raw
      .split(',').map((e) => e.trim()).filter(Boolean);
    const { additional_emails_raw, ...rest } = form;
    onSave({ ...rest, additional_emails });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-form-modal">
        <div className="p-6 border-b border-[#e7e7e7] flex justify-between"><h2 className="text-xl font-semibold">{customer ? 'Edit Customer' : 'New Customer'}</h2><button onClick={onClose}><X size={20} /></button></div>
        <div className="p-6 space-y-3">
          <F label="Email"><input className="inp" type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} data-testid="cust-email-input" disabled={!!customer} /></F>
          <F label="Additional CC Emails (comma-separated)">
            <input className="inp" placeholder="finance@company.com, billing@company.com" value={form.additional_emails_raw} onChange={(e) => upd('additional_emails_raw', e.target.value)} data-testid="cust-additional-emails-input" />
          </F>
          <F label="Name"><input className="inp" value={form.name} onChange={(e) => upd('name', e.target.value)} data-testid="cust-name-input" /></F>
          <F label="Company"><input className="inp" value={form.company} onChange={(e) => upd('company', e.target.value)} /></F>
          <F label="Phone"><input className="inp" value={form.phone} onChange={(e) => upd('phone', e.target.value)} /></F>
          <F label="Address"><input className="inp" value={form.address} onChange={(e) => upd('address', e.target.value)} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="City"><input className="inp" value={form.city} onChange={(e) => upd('city', e.target.value)} /></F>
            <F label="State"><input className="inp" value={form.state} onChange={(e) => upd('state', e.target.value)} /></F>
            <F label="Zip"><input className="inp" value={form.zip_code} onChange={(e) => upd('zip_code', e.target.value)} /></F>
          </div>
          <F label="Notes"><textarea className="inp" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} /></F>
          <label className="flex items-center gap-2 text-sm mt-2">
            <input type="checkbox" checked={form.allow_partial_payment} onChange={(e) => upd('allow_partial_payment', e.target.checked)} data-testid="partial-payment-toggle" />
            <span>Allow partial payments</span>
          </label>
        </div>
        <div className="p-6 border-t border-[#e7e7e7] flex justify-end gap-2"><button onClick={onClose} className="btn-ghost">Cancel</button><button onClick={handleSave} className="btn-brand" data-testid="save-customer-btn">Save</button></div>
      </div>
      <style>{`.inp{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;} .inp:focus{outline:none;border-color:#d92027;}`}</style>
    </div>
  );
}

function CustomerEditModal({ customer, products, onClose, onSave, onReload }) {
  const [tab, setTab] = useState('info');
  const [hidden, setHidden] = useState(new Set(customer.hidden_products || []));
  const [pricing, setPricing] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/customers/${customer.customer_id}`).then((r) => {
      const map = {};
      (r.data.pricing || []).forEach((p) => { map[p.product_id] = p.price; });
      setPricing(map);
      setInvoices(r.data.invoices || []);
      setOrders(r.data.orders || []);
      setLoaded(true);
    });
  }, [customer.customer_id]);

  const toggleHidden = async (pid) => {
    const next = new Set(hidden);
    next.has(pid) ? next.delete(pid) : next.add(pid);
    setHidden(next);
    await adminApi.patch(`/admin/customers/${customer.customer_id}`, { hidden_products: Array.from(next) });
  };
  const setPrice = async (pid, price) => {
    setPricing({ ...pricing, [pid]: price });
    await adminApi.post(`/admin/customers/${customer.customer_id}/pricing`, { product_id: pid, price });
    toast.success('Price updated');
  };
  const resetPrice = async (pid) => {
    const next = { ...pricing }; delete next[pid]; setPricing(next);
    await adminApi.delete(`/admin/customers/${customer.customer_id}/pricing/${pid}`);
    toast.success('Reset to default');
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-edit-modal">
        <div className="p-6 border-b border-[#e7e7e7] flex justify-between">
          <div>
            <h2 className="text-xl font-semibold">{customer.name}</h2>
            <div className="text-xs text-[#6b7280]">{customer.email}</div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="border-b border-[#e7e7e7] flex overflow-x-auto">
          <button onClick={() => setTab('info')} className={`px-5 py-3 text-sm font-semibold whitespace-nowrap ${tab === 'info' ? 'text-[#d92027] border-b-2 border-[#d92027]' : 'text-[#6b7280]'}`} data-testid="tab-info">Info</button>
          <button onClick={() => setTab('history')} className={`px-5 py-3 text-sm font-semibold whitespace-nowrap ${tab === 'history' ? 'text-[#d92027] border-b-2 border-[#d92027]' : 'text-[#6b7280]'}`} data-testid="tab-history">
            History {loaded && <span className="ml-1 text-xs text-[#9ca3af]">({invoices.length})</span>}
          </button>
          <button onClick={() => setTab('catalog')} className={`px-5 py-3 text-sm font-semibold whitespace-nowrap ${tab === 'catalog' ? 'text-[#d92027] border-b-2 border-[#d92027]' : 'text-[#6b7280]'}`} data-testid="tab-catalog">Catalog & Pricing</button>
        </div>
        {tab === 'info' ? (
          <CustomerInfoEditor customer={customer} onSave={onSave} onClose={onClose} />
        ) : tab === 'history' ? (
          <CustomerHistory invoices={invoices} orders={orders} onClose={onClose} />
        ) : (
          <div className="p-6 space-y-3">
            <div className="text-sm text-[#6b7280]">Hide products or set customer-specific prices. Changes save instantly.</div>
            <div className="space-y-2">
              {products.map((p) => {
                const customPrice = pricing[p.product_id];
                const isHidden = hidden.has(p.product_id);
                return (
                  <div key={p.product_id} className="flex items-center justify-between p-3 border border-[#f3f3f3] rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-[#6b7280]">Default: {money(p.default_price)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="number" step="0.01" value={customPrice ?? ''} placeholder={String(p.default_price)} onBlur={(e) => { const v = Number(e.target.value); if (v > 0 && v !== customPrice) setPrice(p.product_id, v); }} onChange={(e) => setPricing({ ...pricing, [p.product_id]: e.target.value })} className="w-24 px-2 py-1 border border-[#e7e7e7] rounded text-sm mono" data-testid={`custom-price-${p.product_id}`} />
                      {customPrice !== undefined && <button onClick={() => resetPrice(p.product_id)} className="text-xs text-[#6b7280]">reset</button>}
                      <button onClick={() => toggleHidden(p.product_id)} data-testid={`toggle-hidden-${p.product_id}`} className={`p-1.5 rounded ${isHidden ? 'bg-[#fde8e8] text-[#d92027]' : 'bg-green-50 text-green-700'}`} title={isHidden ? 'Hidden from customer' : 'Visible to customer'}>
                        {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerInfoEditor({ customer, onSave, onClose }) {
  const [form, setForm] = useState({
    name: customer.name || '', company: customer.company || '', phone: customer.phone || '',
    address: customer.address || '', city: customer.city || '', state: customer.state || '',
    zip_code: customer.zip_code || '', notes: customer.notes || '', allow_partial_payment: customer.allow_partial_payment || false,
    additional_emails_raw: (customer.additional_emails || []).join(', '),
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const handleSave = () => {
    const additional_emails = form.additional_emails_raw
      .split(',').map((e) => e.trim()).filter(Boolean);
    const { additional_emails_raw, ...rest } = form;
    onSave({ ...rest, additional_emails });
  };
  return (
    <>
      <div className="p-6 space-y-3">
        <F label="Name"><input className="inp" value={form.name} onChange={(e) => upd('name', e.target.value)} /></F>
        <F label="Company"><input className="inp" value={form.company} onChange={(e) => upd('company', e.target.value)} /></F>
        <F label="Phone"><input className="inp" value={form.phone} onChange={(e) => upd('phone', e.target.value)} /></F>
        <F label="Additional CC Emails (comma-separated)">
          <input className="inp" placeholder="finance@company.com, billing@company.com" value={form.additional_emails_raw} onChange={(e) => upd('additional_emails_raw', e.target.value)} data-testid="edit-additional-emails-input" />
        </F>
        <F label="Address"><input className="inp" value={form.address} onChange={(e) => upd('address', e.target.value)} /></F>
        <div className="grid grid-cols-3 gap-3">
          <F label="City"><input className="inp" value={form.city} onChange={(e) => upd('city', e.target.value)} /></F>
          <F label="State"><input className="inp" value={form.state} onChange={(e) => upd('state', e.target.value)} /></F>
          <F label="Zip"><input className="inp" value={form.zip_code} onChange={(e) => upd('zip_code', e.target.value)} /></F>
        </div>
        <F label="Notes"><textarea className="inp" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} /></F>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.allow_partial_payment} onChange={(e) => upd('allow_partial_payment', e.target.checked)} data-testid="edit-partial-toggle" />
          <span>Allow partial payments</span>
        </label>
      </div>
      <div className="p-6 border-t border-[#e7e7e7] flex justify-end gap-2"><button onClick={onClose} className="btn-ghost">Cancel</button><button onClick={handleSave} className="btn-brand" data-testid="save-customer-edit-btn">Save</button></div>
      <style>{`.inp{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;}`}</style>
    </>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">{label}</div>{children}</label>;
}

function CustomerHistory({ invoices, orders, onClose }) {
  const totalBilled = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (i.paid_amount || 0), 0);
  const balance = totalBilled - totalPaid;
  const pendingOrders = orders.filter((o) => o.status === 'pending').length;

  return (
    <div className="p-6 space-y-5" data-testid="customer-history-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Orders" value={invoices.length} />
        <Stat label="Billed" value={money(totalBilled)} />
        <Stat label="Paid" value={money(totalPaid)} tint="green" />
        <Stat label="Balance" value={money(balance)} tint="red" />
      </div>

      {/* Pending quote requests (if any) */}
      {pendingOrders > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide mb-2">
            Pending Requests ({pendingOrders})
          </h3>
          <div className="space-y-2">
            {orders.filter((o) => o.status === 'pending').map((o) => {
              const units = (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
              return (
                <div key={o.order_id} className="flex items-center justify-between p-3 border border-[#f3f3f3] rounded-lg text-sm">
                  <div>
                    <div className="font-semibold mono">{o.order_number}</div>
                    <div className="text-xs text-[#6b7280]">{fmtDate(o.created_at)} · {units} units</div>
                  </div>
                  <Link
                    to="/admin/requests"
                    onClick={onClose}
                    className="text-[#d92027] text-xs font-semibold hover:underline inline-flex items-center gap-1"
                    data-testid={`review-order-${o.order_id}`}
                  >
                    Review <ExternalLink size={12} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invoice history */}
      <div>
        <h3 className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide mb-2">
          Order History
        </h3>
        {invoices.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#6b7280] border border-dashed border-[#e7e7e7] rounded-lg">
            No orders yet.
          </div>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="border border-[#f3f3f3] rounded-lg overflow-hidden hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Order #</th>
                    <th className="text-left px-3 py-2">Issued</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Balance</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const bal = (inv.total || 0) - (inv.paid_amount || 0);
                    return (
                      <tr
                        key={inv.invoice_id}
                        className="border-t border-[#f3f3f3] hover:bg-[#fafafa]"
                        data-testid={`history-inv-${inv.invoice_id}`}
                      >
                        <td className="px-3 py-2">
                          <Link
                            to={`/admin/orders/${inv.invoice_id}`}
                            onClick={onClose}
                            className="mono font-semibold text-[#d92027] hover:underline"
                            data-testid={`history-inv-link-${inv.invoice_id}`}
                          >
                            {inv.invoice_number}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-[#6b7280] text-xs mono">{fmtDate(inv.issue_date)}</td>
                        <td className="px-3 py-2">
                          <span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span>
                        </td>
                        <td className="text-right px-3 py-2 mono">{money(inv.total)}</td>
                        <td className={`text-right px-3 py-2 mono font-semibold ${bal > 0 ? 'text-[#d92027]' : 'text-green-700'}`}>
                          {money(bal)}
                        </td>
                        <td className="text-right px-2 py-2">
                          <Link
                            to={`/admin/orders/${inv.invoice_id}`}
                            onClick={onClose}
                            className="p-1.5 inline-flex text-[#6b7280] hover:text-[#0a0a0a]"
                            title="Open"
                          >
                            <ExternalLink size={14} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <div className="md:hidden space-y-2">
              {invoices.map((inv) => {
                const bal = (inv.total || 0) - (inv.paid_amount || 0);
                return (
                  <Link
                    to={`/admin/orders/${inv.invoice_id}`}
                    onClick={onClose}
                    key={inv.invoice_id}
                    className="block border border-[#f3f3f3] rounded-lg p-3 hover:bg-[#fafafa]"
                    data-testid={`m-history-inv-${inv.invoice_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="mono font-semibold text-[#d92027] text-sm truncate">{inv.invoice_number}</div>
                        <div className="text-[11px] text-[#6b7280] mono mt-0.5">{fmtDate(inv.issue_date)}</div>
                      </div>
                      <span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-[#f3f3f3]">
                      <div>
                        <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Total</div>
                        <div className="mono font-semibold">{money(inv.total)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Balance</div>
                        <div className={`mono font-semibold ${bal > 0 ? 'text-[#d92027]' : 'text-green-700'}`}>{money(bal)}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tint }) {
  const tints = { green: 'text-green-700', red: 'text-[#d92027]' };
  return (
    <div className="p-3 border border-[#f3f3f3] rounded-lg">
      <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{label}</div>
      <div className={`text-lg font-semibold mono ${tints[tint] || ''}`}>{value}</div>
    </div>
  );
}
